import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CartDrawer from '../home/components/CartDrawer';
import { useSEO } from '../../hooks/useSEO';

export default function ShippingPage() {
  const [activeSection, setActiveSection] = useState('order-processing');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://motionboyz.org';

  useSEO({
    title: 'Shipping Policy | Motion Boyz – Delivery & Shipping Info',
    description:
      'Learn about Motion Boyz shipping policy. Order processing times, delivery estimates, international shipping, tracking information, and lost package procedures.',
    canonical: '/shipping',
    ogImage: `${siteUrl}/og-image.jpg`,
    ogType: 'article',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const sectionIds = [
    'order-processing',
    'shipping-times',
    'shipping-rates',
    'tracking',
    'lost-stolen',
    'incorrect-address',
    'international',
    'damaged-items',
    'pre-order',
    'contact',
  ];

  useEffect(() => {
    const handleScroll = () => {
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= 300) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'order-processing', label: '1. Order Processing' },
    { id: 'shipping-times', label: '2. Shipping Times' },
    { id: 'shipping-rates', label: '3. Shipping Rates' },
    { id: 'tracking', label: '4. Tracking Information' },
    { id: 'lost-stolen', label: '5. Lost or Stolen Packages' },
    { id: 'incorrect-address', label: '6. Incorrect Address Policy' },
    { id: 'international', label: '7. International Orders' },
    { id: 'damaged-items', label: '8. Damaged Items' },
    { id: 'pre-order', label: '9. Pre-Order Items' },
    { id: 'contact', label: '10. Contact Information' },
  ];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <CartDrawer />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/58edf040d576283f95ca6ae7d9df1926.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <div className="relative z-10 text-center px-6 max-w-4xl w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 mb-6">
            <i className="ri-truck-line text-[#00d4ff] text-sm" />
            <span className="text-[#00d4ff] text-xs tracking-widest uppercase font-semibold">Policy</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            Shipping Policy
          </h1>
          <p className="text-gray-400 text-base tracking-wide max-w-2xl mx-auto">
            Everything you need to know about how we get your order to you
          </p>
          <p className="text-[#00d4ff] text-sm mt-4 tracking-wider">
            Effective Date: <span className="font-semibold">February 27, 2026</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[90%] max-w-[1400px] mx-auto py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar Navigation */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-gray-500 text-xs tracking-widest uppercase font-semibold mb-4 px-4">
                Quick Navigation
              </p>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm tracking-wide rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSection === section.id
                      ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-l-2 border-[#00d4ff]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-4xl">

            {/* Info Banner */}
            <div className="mb-12 p-5 bg-[#111] border border-[#00d4ff]/10 rounded-xl flex flex-wrap gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-building-line text-[#00d4ff]" />
                <span><span className="text-white font-semibold">Business:</span> Motion Boyz</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-global-line text-[#00d4ff]" />
                <span><span className="text-white font-semibold">Website:</span> motionboyz.org</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-[#00d4ff]" />
                <span><span className="text-white font-semibold">Effective:</span> February 27, 2026</span>
              </div>
            </div>

            {/* 1. Order Processing */}
            <section id="order-processing" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">1. Order Processing</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>All orders are processed within <strong className="text-white">2–5 business days</strong> (excluding weekends and holidays).</p>
                <p>During high-volume periods such as limited drops, restocks, or promotions, processing times may be extended to <strong className="text-white">5–10 business days</strong>.</p>
                <p>You will receive a confirmation email once your order has been placed and a tracking number once your order has shipped.</p>
              </div>
            </section>

            {/* 2. Shipping Times */}
            <section id="shipping-times" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">2. Shipping Times</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Estimated delivery times after processing:</p>
                <ul className="space-y-3 mt-2">
                  {[
                    { label: 'U.S. Standard Shipping', time: '3–7 business days' },
                    { label: 'U.S. Expedited Shipping', time: '2–3 business days' },
                    { label: 'International Shipping', time: '7–21 business days' },
                  ].map((item) => (
                    <li key={item.label} className="flex items-center justify-between p-4 bg-[#111] border border-[#00d4ff]/10 rounded-xl">
                      <span className="text-white font-semibold text-sm">{item.label}</span>
                      <span className="text-[#00d4ff] text-sm font-medium">{item.time}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-gray-500 text-sm italic mt-2">Shipping times are estimates and are not guaranteed.</p>
              </div>
            </section>

            {/* 3. Shipping Rates */}
            <section id="shipping-rates" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">3. Shipping Rates</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Shipping costs are calculated at checkout based on:</p>
                <ul className="space-y-2 pl-4">
                  {['Location', 'Package weight', 'Shipping method selected'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>We may offer free shipping promotions at our discretion.</p>
              </div>
            </section>

            {/* 4. Tracking Information */}
            <section id="tracking" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">4. Tracking Information</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Once your order has shipped, you will receive a tracking number via email.</p>
                <p>Please allow <strong className="text-white">24–48 hours</strong> for tracking information to update.</p>
              </div>
            </section>

            {/* 5. Lost or Stolen Packages */}
            <section id="lost-stolen" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">5. Lost or Stolen Packages</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Motion Boyz is <strong className="text-white">not responsible</strong> for:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'Lost packages marked as delivered',
                    'Stolen packages',
                    'Incorrect shipping addresses entered at checkout',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>If your tracking states &quot;Delivered&quot; but you did not receive your package, please contact the carrier directly.</p>
              </div>
            </section>

            {/* 6. Incorrect Address Policy */}
            <section id="incorrect-address" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">6. Incorrect Address Policy</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Customers are responsible for entering accurate shipping information.</p>
                <p>If an order is returned due to an incorrect address:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'The customer will be responsible for reshipping fees',
                    'Refunds will exclude original shipping charges',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* 7. International Orders */}
            <section id="international" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">7. International Orders</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Customers are responsible for:</p>
                <ul className="space-y-2 pl-4">
                  {['Customs fees', 'Import taxes', 'Duties charged by their country'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>Motion Boyz is not responsible for customs delays.</p>
              </div>
            </section>

            {/* 8. Damaged Items */}
            <section id="damaged-items" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">8. Damaged Items</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>If your order arrives damaged:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'Contact us within 48 hours of delivery',
                    'Provide photos of packaging and item',
                    'Include order number',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>We will review and determine appropriate resolution.</p>
              </div>
            </section>

            {/* 9. Pre-Order Items */}
            <section id="pre-order" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">9. Pre-Order Items</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>If your order contains a pre-order item:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'The entire order will ship once the pre-order item is ready',
                    'Estimated ship dates will be clearly stated on product pages',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* 10. Contact Information */}
            <section id="contact" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">10. Contact Information</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>For shipping questions, contact:</p>
                <div className="bg-[#111] border border-[#00d4ff]/10 rounded-xl p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff]">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Email</p>
                      <a href="mailto:llc.motionboyz@gmail.com" className="text-[#00d4ff] hover:underline cursor-pointer text-sm">
                        llc.motionboyz@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff]">
                      <i className="ri-building-line text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Business Name</p>
                      <p className="text-gray-400 text-sm">Motion Boyz</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bottom CTA */}
            <div className="mt-16 p-8 bg-gradient-to-br from-[#00d4ff]/5 to-transparent border border-[#00d4ff]/20 rounded-2xl text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] mx-auto mb-4">
                <i className="ri-shopping-bag-line text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Ready to Order?</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Explore our exclusive collection of premium limited streetwear designed for those who never stop moving.
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/shop')}
                className="px-8 py-3 bg-[#00d4ff] text-black font-bold text-sm tracking-widest uppercase rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 0 30px rgba(0,212,255,0.4)' }}
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
